<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, toRefs, watch} from 'vue';
import {useRoute} from 'vue-router';
import {useAppSession} from '../core/app-session';
import {useNotificationExpiry, useNotifications} from '../core/notifications';
import Navbar from './layout/Navbar.vue';
import VersionNotification from './layout/VersionNotification.vue';
import {acceptReferral} from '../api/users';
import {acceptStoredReferral} from './referrals/referrals';
import LanguageSwitcher from '../i18n/LanguageSwitcher.vue';
import {parseLocalePath} from '../i18n/locale-path';

const appSession = useAppSession();
const route = useRoute();
const hasLanguageVersions = computed(() => parseLocalePath(route.path) !== null);
const notifications = useNotifications();
const {warnings, infos} = toRefs(notifications.state);
useNotificationExpiry(notifications.state);

async function associateStoredReferral() {
    if (!appSession.state.auth?.user?.id) return;
    try {
        await acceptStoredReferral(localStorage, acceptReferral);
    } catch {
        // Keep the invitation in storage and retry on the next authenticated app load.
    }
}

watch(() => appSession.state.auth?.user?.id, associateStoredReferral, {immediate: true});

// Refresh the current user's subscription and store it in the session state.
function refreshSubscriptionStatus() {
    if (!appSession.state.auth?.user?.id) {
        return;
    }
    void appSession.refreshSubscription(appSession.state.auth.user.id);
}

// Re-check the subscription only when the app tab becomes visible again.
function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        refreshSubscriptionStatus();
    }
}

// Register the visibility listener when the root app mounts.
onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Warm up Paddle on the landing page so checkout opens without reinitializing the SDK.
    if (appSession.state.auth) {
        void appSession.initializePaddle().catch((): undefined => undefined);
    }
});

// Remove the visibility listener when the root app unmounts.
onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>
<template>
    <Navbar></Navbar>
    <div class="flex flex-grow flex-col px-3">
        <div class="toast toast-top toast-center z-50">
            <template v-if="infos.length">
                <div
                    v-for="infoMessage in infos"
                    :key="infoMessage.id"
                    class="alert alert-info">
                    <span>{{ infoMessage.message }}</span>
                </div>
            </template>
            <template v-if="warnings.length">
                <div
                    v-for="warningMessage in warnings"
                    :key="warningMessage.id"
                    class="alert alert-warning">
                    <span>{{ warningMessage.message }}</span>
                </div>
            </template>
        </div>
        <router-view></router-view>
    </div>
    <footer
        v-if="hasLanguageVersions"
        data-testid="public-footer"
        class="border-base-300 flex justify-center border-t px-3 py-6 text-sm">
        <LanguageSwitcher />
    </footer>
    <VersionNotification />
    <datalist id="presetColors">
        <!-- 32 colors -->
        <!-- Grayscale -->
        <option value="#FFFFFF"></option>
        <option value="#EEEEEE"></option>
        <option value="#BDBDBD"></option>
        <option value="#757575"></option>
        <option value="#424242"></option>
        <option value="#000000"></option>

        <!-- Material UI Colors -->
        <option
            value="#F44336"
            :label="$t('inspector.colors.red')"></option>
        <option
            value="#E91E63"
            :label="$t('inspector.colors.pink')"></option>
        <option
            value="#9C27B0"
            :label="$t('inspector.colors.purple')"></option>
        <option
            value="#673AB7"
            :label="$t('inspector.colors.deepPurple')"></option>
        <option
            value="#3F51B5"
            :label="$t('inspector.colors.indigo')"></option>
        <option
            value="#2196F3"
            :label="$t('inspector.colors.blue')"></option>
        <option
            value="#03A9F4"
            :label="$t('inspector.colors.lightBlue')"></option>
        <option
            value="#00BCD4"
            :label="$t('inspector.colors.cyan')"></option>
        <option
            value="#009688"
            :label="$t('inspector.colors.teal')"></option>
        <option
            value="#4CAF50"
            :label="$t('inspector.colors.green')"></option>
        <option
            value="#8BC34A"
            :label="$t('inspector.colors.lightGreen')"></option>
        <option
            value="#CDDC39"
            :label="$t('inspector.colors.lime')"></option>
        <option
            value="#FFEB3B"
            :label="$t('inspector.colors.yellow')"></option>
        <option
            value="#FFC107"
            :label="$t('inspector.colors.amber')"></option>
        <option
            value="#FF8200"
            :label="$t('inspector.colors.orange')"></option>
        <option
            value="#FF5722"
            :label="$t('inspector.colors.deepOrange')"></option>
        <option
            value="#795548"
            :label="$t('inspector.colors.brown')"></option>
        <option
            value="#607D8B"
            :label="$t('inspector.colors.blueGrey')"></option>
    </datalist>
</template>
<style lang="css">
body {
    visibility: visible !important;
}

.pixelated {
    image-rendering: pixelated;
}
</style>
